import { type ReactNode, useEffect, useId, useMemo, useRef, useState } from 'react';
import { cn } from '@doccareplus/shared/lib/utils';

export interface SearchSelectOption {
  /** Stable value reported to `onChange`. */
  value: string;
  /** Primary text shown in the row and trigger. */
  label: string;
  /** Secondary line — only rendered in the `rich` variant. */
  description?: string;
  /** Explicit avatar text (initials). When omitted, initials are derived from `label`. */
  avatarText?: string;
  /** Optional avatar background (any CSS colour / gradient). Defaults to the brand gradient. */
  avatarColor?: string;
  /** Custom leading node (e.g. an icon or flag). Overrides the avatar in the `rich` variant. */
  leading?: ReactNode;
  disabled?: boolean;
}

export interface SearchSelectProps {
  options: SearchSelectOption[];
  value: string | null;
  onChange: (value: string) => void;
  /** Optional callback for async search scenarios. Called whenever the search input changes. */
  onSearchChange?: (query: string) => void;
  /** `plain` = label-only rows (languages, statuses). `rich` = avatar + description (people, clinics). */
  variant?: 'plain' | 'rich';
  /** Hide the leading avatar in `rich` variant, e.g. when rows only need a label + description. Default `true`. */
  showAvatar?: boolean;
  /**
   * Trigger look. `select` (default) = standalone dropdown (surface bg, visible border).
   * `input` = matches the `Input` field exactly (h-12, surface-2 fill, transparent border)
   * so it can sit in a form grid beside text inputs.
   */
  appearance?: 'select' | 'input';
  /** Toggle the in-popover search box. Default `true`; set `false` for tiny fixed lists. */
  searchable?: boolean;
  /** Whether the trigger renders the selected option's description as a second line (rich only). */
  showDescriptionInTrigger?: boolean;
  placeholder?: string;
  label?: string;
  error?: string;
  /** Show a red "*" next to the label. */
  requiredMark?: boolean;
  /** Show a muted "(optional)" tag next to the label. Ignored when `requiredMark`. */
  optional?: boolean;
  disabled?: boolean;
  id?: string;
  size?: 'sm' | 'md';
  fullWidth?: boolean;
  /** Popover direction. Use `top` when the control sits near the bottom of the viewport. */
  placement?: 'bottom' | 'top';
  className?: string;
  /** Optional node rendered below the option list inside the popover (e.g. a "Create new…" action). */
  footer?: ReactNode;
  // ── Caller-supplied, translatable strings (this package stays i18n-free) ──
  searchPlaceholder?: string;
  noResultsLabel?: string;
  triggerAriaLabel?: string;
  /** Use combobox semantics when the trigger represents an editable form field. */
  triggerRole?: 'button' | 'combobox';
  /** Optional native mirror for assistive technology and form integrations. */
  nativeSelectLabel?: string;
}

function initialsOf(label: string): string {
  const parts = label.split(/\s+/).filter(Boolean);
  const letters = parts.slice(0, 2).map((p) => p[0]).join('');
  return (letters || label.slice(0, 2)).toUpperCase();
}

/**
 * SearchSelect — pure presentational combobox with an optional search field.
 *
 * Controlled: it reports the chosen value via `onChange` and owns no business rules.
 * All user-visible strings are supplied by the caller so the consuming app can translate
 * them (mirrors `PhoneInput`). Styling derives entirely from design-system tokens.
 */
export function SearchSelect({
  options,
  value,
  onChange,
  variant = 'plain',
  showAvatar = true,
  appearance = 'input',
  searchable = true,
  showDescriptionInTrigger = false,
  placeholder,
  label,
  error,
  requiredMark,
  optional,
  disabled,
  id,
  size = 'md',
  fullWidth = true,
  placement = 'bottom',
  className,
  footer,
  searchPlaceholder = 'Search',
  noResultsLabel = 'No results',
  triggerAriaLabel,
  triggerRole = 'button',
  nativeSelectLabel,
  onSearchChange,
}: SearchSelectProps) {
  const rich = variant === 'rich';
  const reactId = useId();
  const fieldId = id ?? `search-select-${reactId}`;
  const listboxId = `${fieldId}-listbox`;

  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [activeIndex, setActiveIndex] = useState(-1);
  const containerRef = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    onSearchChange?.(query);
  }, [query, onSearchChange]);

  const selected = useMemo(() => options.find((o) => o.value === value) ?? null, [options, value]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) {
      return options;
    }
    return options.filter(
      (o) => o.label.toLowerCase().includes(q) || (o.description?.toLowerCase().includes(q) ?? false)
    );
  }, [options, query]);

  // Close on outside pointer press.
  useEffect(() => {
    if (!open) {
      return;
    }
    const onPointerDown = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', onPointerDown);
    return () => document.removeEventListener('mousedown', onPointerDown);
  }, [open]);

  // Focus the search box after the popover has mounted.
  useEffect(() => {
    if (!open || !searchable) return;
    const timer = setTimeout(() => searchRef.current?.focus(), 0);
    return () => clearTimeout(timer);
  }, [open, searchable]);

  const toggleOpen = () => {
    if (!open) {
      setQuery('');
      setActiveIndex(-1);
    }
    setOpen((current) => !current);
  };

  const close = () => setOpen(false);

  const commit = (option: SearchSelectOption) => {
    if (option.disabled) {
      return;
    }
    onChange(option.value);
    close();
  };

  const onKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Escape') {
      event.preventDefault();
      close();
      return;
    }
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setActiveIndex((i) => Math.min(i + 1, filtered.length - 1));
      return;
    }
    if (event.key === 'ArrowUp') {
      event.preventDefault();
      setActiveIndex((i) => Math.max(i - 1, 0));
      return;
    }
    if (event.key === 'Enter') {
      event.preventDefault();
      const option = filtered[activeIndex];
      if (option) {
        commit(option);
      }
    }
  };

  const inputLike = appearance === 'input';
  const triggerHeight = size === 'sm' ? 'h-10' : inputLike ? 'h-12' : 'h-11';

  const renderAvatar = (option: SearchSelectOption, dim: string) =>
    option.leading ?? (
      <span
        aria-hidden="true"
        className={cn('grid shrink-0 place-items-center rounded-[9px] text-[11px] font-bold text-white', dim)}
        style={{ background: option.avatarColor ?? 'linear-gradient(135deg, var(--brand-500), var(--brand-700))' }}
      >
        {option.avatarText ?? initialsOf(option.label)}
      </span>
    );

  return (
    <div className={cn('flex flex-col gap-[7px]', fullWidth ? 'w-full' : 'w-max', className)}>
      {label && (
        <label htmlFor={fieldId} className="block text-[12.5px] font-semibold tracking-[0.01em] text-[var(--text-2)]">
          {label}
          {requiredMark ? (
            <span aria-hidden="true" className="ml-0.5 text-[var(--danger)]">
              *
            </span>
          ) : optional ? (
            <span aria-hidden="true" className="ml-1 font-normal text-[var(--text-3)]">
              (optional)
            </span>
          ) : null}
        </label>
      )}

      <div className="relative" ref={containerRef} onKeyDown={onKeyDown}>
        {nativeSelectLabel && (
          <select
            aria-label={nativeSelectLabel}
            value={value ?? ''}
            onChange={(event) => onChange(event.target.value)}
            className="sr-only"
            tabIndex={-1}
          >
            {value == null && <option value="">{placeholder}</option>}
            {options.map((option) => (
              <option key={option.value} value={option.value} disabled={option.disabled}>{option.label}</option>
            ))}
          </select>
        )}
        <button
          id={fieldId}
          type="button"
          role={triggerRole}
          value={value ?? ''}
          aria-haspopup="listbox"
          aria-expanded={open}
          aria-required={requiredMark || undefined}
          aria-controls={open ? listboxId : undefined}
          aria-label={nativeSelectLabel ? undefined : triggerAriaLabel}
          disabled={disabled}
          onClick={toggleOpen}
          onChange={(event) => {
            const nextValue = event.currentTarget.value;
            if (options.some((option) => option.value === nextValue)) onChange(nextValue);
          }}
          className={cn(
            'flex w-full items-center gap-2.5 rounded-[12px] border text-left',
            triggerHeight,
            inputLike ? 'px-[15px]' : 'px-3',
            'text-[var(--text)] transition-[background-color,border-color,box-shadow] duration-200',
            inputLike
              ? 'bg-[var(--surface-2)] hover:bg-[var(--surface-3)]'
              : 'bg-[var(--surface)] hover:bg-[var(--surface-2)] hover:border-[var(--border-strong)]',
            'disabled:cursor-not-allowed disabled:opacity-60',
            open
              ? 'border-[var(--primary)] ring-4 ring-[var(--ring)] bg-[var(--surface)]'
              : inputLike
                ? 'border-transparent'
                : 'border-[var(--border)]',
            error && 'border-[var(--danger)]'
          )}
        >
          {rich && showAvatar && selected && renderAvatar(selected, 'h-[30px] w-[30px]')}
          <span className="min-w-0 flex-1">
            {selected ? (
              <>
                <span
                  className={cn(
                    'block truncate text-[var(--text)]',
                    inputLike ? 'text-[14.5px] font-normal' : 'text-[13.5px] font-semibold'
                  )}
                >
                  {selected.label}
                </span>
                {rich && showDescriptionInTrigger && selected.description && (
                  <span className="block truncate text-[11.5px] font-medium text-[var(--text-3)]">
                    {selected.description}
                  </span>
                )}
              </>
            ) : (
              <span
                className={cn(
                  'block truncate text-[var(--text-3)]',
                  inputLike ? 'text-[14.5px] font-normal' : 'text-[13.5px] font-medium'
                )}
              >
                {placeholder}
              </span>
            )}
          </span>
          <svg
            className={cn('h-4 w-4 shrink-0 text-[var(--text-3)] transition-transform duration-200', open && 'rotate-180')}
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            viewBox="0 0 24 24"
            aria-hidden="true"
          >
            <path strokeLinecap="round" strokeLinejoin="round" d="M6 9l6 6 6-6" />
          </svg>
        </button>

        {open && (
          <div
            className={cn(
              'absolute left-0 right-0 z-50 rounded-[13px] border border-[var(--border)] bg-[var(--surface)] p-1.5 shadow-[var(--shadow-3)]',
              'animate-[fadeIn_0.14s_ease-out]',
              placement === 'top' ? 'bottom-[calc(100%+6px)]' : 'top-[calc(100%+6px)]'
            )}
          >
            {searchable && (
              <div className="mb-1.5 flex items-center gap-2 rounded-[9px] border border-transparent bg-[var(--surface-2)] px-2.5 focus-within:border-[var(--border-strong)] focus-within:bg-[var(--surface)]">
                <svg className="h-4 w-4 shrink-0 text-[var(--text-3)]" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24" aria-hidden="true">
                  <circle cx="11" cy="11" r="7" />
                  <path strokeLinecap="round" strokeLinejoin="round" d="m21 21-4.3-4.3" />
                </svg>
                <input
                  ref={searchRef}
                  type="text"
                  value={query}
                  onChange={(e) => {
                    setQuery(e.target.value);
                    setActiveIndex(-1);
                  }}
                  placeholder={searchPlaceholder}
                  aria-label={searchPlaceholder}
                  aria-controls={listboxId}
                  className="h-9 flex-1 border-none bg-transparent text-[13px] font-medium text-[var(--text)] outline-none placeholder:text-[var(--text-3)]"
                />
              </div>
            )}

            <ul id={listboxId} role="listbox" className="flex max-h-[248px] flex-col gap-px overflow-y-auto">
              {filtered.length === 0 ? (
                <li className="px-3 py-5 text-center text-[12.5px] text-[var(--text-3)]">{noResultsLabel}</li>
              ) : (
                filtered.map((option, index) => {
                  const isSelected = option.value === value;
                  const isActive = index === activeIndex;
                  return (
                    <li key={option.value}>
                      <button
                        type="button"
                        role="option"
                        aria-selected={isSelected}
                        disabled={option.disabled}
                        onClick={() => commit(option)}
                        onMouseEnter={() => setActiveIndex(index)}
                        className={cn(
                          'flex w-full items-center gap-2.5 rounded-[9px] px-2.5 py-2 text-left transition-colors',
                          'text-[13px] font-semibold text-[var(--text-2)]',
                          'disabled:cursor-not-allowed disabled:opacity-50',
                          isActive && 'bg-[var(--surface-2)] text-[var(--text)]',
                          isSelected && 'bg-[var(--primary-weak)] text-[var(--primary)]'
                        )}
                      >
                        {rich && showAvatar && renderAvatar(option, 'h-[30px] w-[30px]')}
                        <span className="min-w-0 flex-1">
                          <span className="block truncate">{option.label}</span>
                          {rich && option.description && (
                            <span
                              className={cn(
                                'block truncate text-[11px] font-medium',
                                isSelected ? 'text-[var(--primary)]' : 'text-[var(--text-3)]'
                              )}
                            >
                              {option.description}
                            </span>
                          )}
                        </span>
                        {isSelected && (
                          <svg className="ml-auto h-4 w-4 shrink-0 text-[var(--primary)]" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24" aria-hidden="true">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M20 6 9 17l-5-5" />
                          </svg>
                        )}
                      </button>
                    </li>
                  );
                })
              )}
            </ul>

            {footer && <div className="mt-1 border-t border-[var(--border)] pt-1">{footer}</div>}
          </div>
        )}
      </div>

      {error && (
        <span className="flex items-center gap-1 text-[12px] font-medium text-[var(--danger)]">
          <svg className="h-3.5 w-3.5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24" aria-hidden="true">
            <path strokeLinecap="round" strokeLinejoin="round" d="M12 8v4m0 4h.01M10.3 3.9L1.8 18a2 2 0 001.7 3h17a2 2 0 001.7-3L13.7 3.9a2 2 0 00-3.4 0z" />
          </svg>
          {error}
        </span>
      )}
    </div>
  );
}
